import React, {useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {QRCodeCanvas} from "qrcode.react";
import Modal from "../../../components/My/Modal";
import {TurnstileButton as Button} from "../components/Button";
import InputField from "../../../FinalForm/InputField";
import {Form} from "react-final-form";
import {apiGetConfig} from "../Config/Actions";

// hardcoded validation token (sdílený API i TCP módem) — beze změny oproti původní verzi
const VALIDATION_TOKEN = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9";
const TCP_PORT = 9878;
const CONFIG_KEY_SERVER_ADDRESS = "ticketvalidator.server_local_address";
const CONFIG_KEY_PAIRING_TOKEN = "ticketvalidator.pairing_token";
const SIZE = 200;

// QR se generuje lokálně (qrcode.react) — data (vč. tokenů) NEopouští prohlížeč.
const ConfigQrModal = ({visible, close}) => {
    const user = useSelector((state) => state.user);

    const [mode, setMode] = useState("api"); // "api" | "tcp"

    // --- API mód stav ---
    const [ipServer, setIpServer] = useState(null);
    const timer = useRef(null);

    // --- TCP mód stav ---
    const [configLoaded, setConfigLoaded] = useState(false);
    const [configHost, setConfigHost] = useState(null);   // host z configu (null = chybí)
    const [pairingToken, setPairingToken] = useState(null); // token z configu (null/"" = chybí)
    const [manualHost, setManualHost] = useState("");       // ruční host když config prázdný

    // fetch configu při přepnutí na TCP (jen jednou)
    useEffect(() => {
        if (mode !== "tcp" || configLoaded || !user.activeAreal) return;
        apiGetConfig(user.activeAreal.id, (result) => {
            const rows = result ?? [];
            const findVal = (key) => {
                const row = rows.find((r) => r.configKey === key);
                const v = row && row.value;
                return v && String(v).trim() !== "" ? String(v).trim() : null;
            };
            setConfigHost(findVal(CONFIG_KEY_SERVER_ADDRESS));
            setPairingToken(findVal(CONFIG_KEY_PAIRING_TOKEN));
            setConfigLoaded(true);
        }, () => {
            setConfigHost(null);
            setPairingToken(null);
            setConfigLoaded(true);
        });
    }, [mode, configLoaded, user.activeAreal]);

    // reset stavu při zavření modalu
    useEffect(() => {
        if (!visible) {
            setMode("api");
            setIpServer(null);
            setConfigLoaded(false);
            setConfigHost(null);
            setPairingToken(null);
            setManualHost("");
        }
    }, [visible]);

    // ---- QR data (JSON stringy) ----
    const apiConfigValue = ipServer
        ? JSON.stringify({ipAddress: ipServer, validationToken: VALIDATION_TOKEN})
        : null;
    const resetValue = JSON.stringify({astaLaVistaBaby: true, validationToken: VALIDATION_TOKEN});

    const tcpHost = configHost || (manualHost.trim() !== "" ? manualHost.trim() : null);
    const tcpConfigValue = tcpHost
        ? JSON.stringify({
            ipAddress: tcpHost,
            port: TCP_PORT,
            pairingToken: pairingToken || "",
            validationToken: VALIDATION_TOKEN,
        })
        : null;

    const renderModeSwitch = () => (
        <div className={"flex flex-row space-x-4 mb-2"}>
            <label className={"flex flex-row items-center space-x-1"}>
                <input type="radio" name="qrMode" checked={mode === "api"} onChange={() => setMode("api")}/>
                <span>Komunikace přes API</span>
            </label>
            <label className={"flex flex-row items-center space-x-1"}>
                <input type="radio" name="qrMode" checked={mode === "tcp"} onChange={() => setMode("tcp")}/>
                <span>Komunikace přes TCP</span>
            </label>
        </div>
    );

    const renderApi = () => (
        <div className={"flex flex-col space-y-2"}>
            <Form onSubmit={() => {}} render={() => (
                <InputField
                    label={"IP adresa + port serveru (příklad 192.168.40.2:6592)"}
                    onChange={(name, value) => {
                        clearTimeout(timer.current);
                        timer.current = setTimeout(() => setIpServer(value), 500);
                    }}
                    name={"ipServer"} isMandatory={true}/>
            )}/>
            {renderQrGrid(apiConfigValue, ipServer ? resetValue : null)}
        </div>
    );

    const renderTcp = () => {
        if (!configLoaded) return <div>Načítám konfiguraci…</div>;
        return (
            <div className={"flex flex-col space-y-2"}>
                {configHost
                    ? <div>
                        <label className={"font-semibold"}>Adresa serveru (z konfigurace):</label>
                        <div>{configHost}:{TCP_PORT}</div>
                    </div>
                    : <Form onSubmit={() => {}} render={() => (
                        <InputField
                            label={`Adresa serveru (host) — v konfiguraci není nastavena, port ${TCP_PORT} fixní`}
                            onChange={(name, value) => setManualHost(value || "")}
                            name={"manualHost"} isMandatory={true}/>
                    )}/>}
                {!pairingToken &&
                    <div className={"text-red-500"}>
                        Pairing token není v konfiguraci nastaven — QR bude vygenerován bez tokenu.
                    </div>}
                {renderQrGrid(tcpConfigValue, tcpHost ? resetValue : null)}
            </div>
        );
    };

    const renderQrGrid = (configValue, resetVal) => (
        <div className={"grid grid-cols-2 gap-2 w-full place-items-end"}>
            {configValue && <div className={"flex flex-col space-y-2"}>
                <h2>Konfigurační QR:</h2>
                <QRCodeCanvas value={configValue} size={SIZE}/>
            </div>}
            {resetVal && <div className={"flex flex-col space-y-2"}>
                <h2>Restart QR:</h2>
                <QRCodeCanvas value={resetVal} size={SIZE}/>
            </div>}
        </div>
    );

    const getBody = () => (
        <div className={"flex flex-col space-y-2"}>
            {renderModeSwitch()}
            {mode === "api" ? renderApi() : renderTcp()}
        </div>
    );

    if (!visible) return null;
    return (
        <Modal body={getBody()} title={"Konfigurační QR"} visible={visible}
               close={() => close(null)}
               footer={<Button outline={true} text={"Zavřít"} onClick={() => close()}/>}
        />
    );
};

export default ConfigQrModal;
